// Terms.tsx
import React from "react";
import { Box, ScrollArea } from "@mantine/core";
import Markdown from "react-markdown";
import { useDarkModeColors } from "@/hooks/helperHooks/useDarkModeColors";

const termsOfUseText = `
# General Conditions

By accessing and using this website, you agree to the following terms and conditions:

### 1. Ownership and Licensing

This website, including all content, the selection, and arrangement of content on each individual page or across the collection of pages, is the exclusive property of D-ICE ENGINEERING or is used with the appropriate licenses unless explicitly stated otherwise.

All computation solvers used are the full and sole property of D-ICE ENGINEERING. All rights are reserved.

### 2. Permitted Use

You are permitted to store extracts from this website on your personal computer and to print copies for your personal, non-commercial use only.

Any other form of use, including but not limited to reproduction, translation, adaptation, arrangement, alteration, distribution, or storage of any part of this site, whether in whole or in part, is strictly prohibited without prior written permission from D-ICE ENGINEERING.

The computed performance data can be used for various purposes, including but not limited to training, education, research, and commercial applications, without any guarantee of performance (see next section).

### 3. Disclaimer of Accuracy

Despite our diligent efforts to ensure the accuracy and currency of the information on this website, we do not warrant or guarantee that the information is free from errors or omissions.

This website is used to make predictions of vessel performance. However, computational results presented on this website should not be construed as performance guarantees. D-ICE ENGINEERING cannot be held liable for any inaccuracies or errors in these calculations.

### 4. Governing Law

These terms and conditions shall be governed by and construed in accordance with the laws of France.

### 5. Hosting and Project Information

This website is hosted by Amazon Web Services (AWS) in Europe (Paris).

The site has been developed by the D-ICE ENGINEERING team as part of the OPERVENT project, which is co-funded by ADEME, The French Agency for Ecological Transition.

### 6. GDPR Compliance

D-ICE ENGINEERING is committed to protecting your personal data and privacy in accordance with the General Data Protection Regulation (GDPR). Any personal data collected through this website will be processed and stored in compliance with GDPR requirements.

You have the right to access, rectify, or delete any personal data we have collected about you. For any requests related to your personal data, please contact our Data Protection Officer at [contact email].

We will not share your personal data with third parties without your explicit consent unless required by law.

By continuing to use this website, you acknowledge that you have read, understood, and agreed to be bound by these terms and conditions.

# Privacy Policy

D-ICE appreciates your visit to our website and your interest in our company.

We are committed to protecting your personal data and ensuring you feel secure while visiting and using Shiplify. This Privacy Policy outlines how we handle your personal information in compliance with the European General Data Protection Regulation (GDPR).

### 1. Data Collection and Usage

#### Personal Data

We do not store any personal information on our servers. Any data you provide is used solely to enhance your navigation experience and the services we offer.

The information we gather, such as cookies or usage data, is used only to improve website functionality and user experience.

#### Performance Predictions

When you use our services to run performance predictions for vessels, the data and results are processed securely.

While we may investigate the results to ensure the accuracy and integrity of the calculations, the content of these calculations will never be used by us for any other purpose.

### 2. Your Rights

Under the GDPR, you have the following rights concerning your personal data:

- **Access**: You have the right to request access to the personal data we hold about you.
- **Rectification**: You can request corrections to any inaccurate or incomplete data.
- **Erasure**: You have the right to request the deletion of your personal data, subject to certain conditions.
- **Restriction of Processing**: You can request that we limit the processing of your personal data under specific circumstances.
- **Data Portability**: You have the right to receive your personal data in a structured, commonly used, and machine-readable format.
- **Objection**: You can object to the processing of your personal data in certain situations.
- **Withdraw Consent**: If you have provided consent for data processing, you can withdraw this consent at any time.

To exercise any of these rights, please contact us at contact@dice-engineering.com.

### 3. Data Security

We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction.

### 4. External Links

Please note that our website may contain links to other sites that follow different privacy practices. We are not responsible for the content or privacy policies of these other sites.

### 5. Changes to This Privacy Policy

We may update this Privacy Policy from time to time. Any changes will be posted on this page, and we will notify you of significant changes where required.

### 6. Contact Information

For any questions or concerns regarding this Privacy Policy or our data practices, please contact:

**Data Protection Officer (DPO)**  
D-ICE Engineering  
Email: contact@dice-engineering.com  

By using our website, you acknowledge that you have read and understood this Privacy Policy, and you agree to its terms.
`;

const Terms: React.FC = () => {
  const { getColor } = useDarkModeColors();
  return (
    <Box
      style={{
        width: "70vw",
        margin: "auto",
        padding: "20px",
        color: getColor("title"),
      }}
    >
      <ScrollArea style={{ height: "100%", padding: "1rem" }}>
        <Markdown>{termsOfUseText}</Markdown>
      </ScrollArea>
    </Box>
  );
};

export default Terms;
